import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Phone, 
  Home, 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Shield, 
  Banknote, 
  Heart, 
  Building,
  User,
  LogIn,
  LogOut,
  Settings,
  ChevronDown,
  ShieldUser,
  BookOpen,
  Search,
  Grid3X3
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  
  const { isAuthenticated, user, userProfile, logout, getFavorites } = useAuth();
  const favorites = getFavorites();

  const mainMenuItems = [
   { 
    title: 'דף הבית', 
    icon: <Home className="w-4 h-4" />, 
    href: '/',
    showInMobile: true
  },
    { 
      title: 'סורק מסלולים', 
      icon: <Search className="w-4 h-4" />, 
      href: '/route-scanner' 
    },
    { 
      title: 'קרנות השתלמות', 
      icon: <BarChart3 className="w-4 h-4" />, 
      href: '/compare/study-funds'
    },
    { 
      title: 'קופות גמל', 
      icon: <Banknote className="w-4 h-4" />, 
      href: '/compare/pension-savings'
    },
    { 
      title: 'גמל להשקעה', 
      icon: <TrendingUp className="w-4 h-4" />, 
      href: '/compare/investment-funds'
    },
    { 
      title: 'פוליסות חיסכון', 
      icon: <Shield className="w-4 h-4" />, 
      href: '/compare/insurance-savings'
    },
  {
    title:'ביטוחי מנהלים',
    icon: <ShieldUser className="w-4 h-4" />,
    href: '/compare/insurance-management'
  },
    { 
      title: 'קרנות פנסיה', 
      icon: <Building className="w-4 h-4" />, 
      href: '/compare/pension-plans'
    },
    { 
      title: 'חיסכון לכל ילד', 
      icon: <Heart className="w-4 h-4" />, 
      href: '/compare/child-savings'
    },
  ];

  const actionItems = [
    { 
      title: 'צור קשר', 
      icon: <Phone className="w-4 h-4" />, 
      href: '/contact' 
    },
    { 
      title: 'מילון מונחים', 
      icon: <BookOpen className="w-4 h-4" />, 
      href: '/dictionary' 
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setShowUserDropdown(false);
    navigate('/');
  };

  const UserDropdown = () => (
    <div 
      ref={dropdownRef}
      className="absolute left-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-50"
    >
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div className="text-white">
            <p className="font-medium text-sm">
              {userProfile?.displayName || 'משתמש'}
            </p>
            <p className="text-blue-100 text-xs" dir="ltr">
              {user?.email}
            </p>
          </div>
        </div>
      </div>

      <div className="p-2">
        <Link
          to="/user/profile"
          onClick={() => setShowUserDropdown(false)}
          className="flex items-center justify-between w-full px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-colors group"
        >
          <div className="flex items-center gap-3">
            <Heart className="w-4 h-4" />
            <span className="font-medium">המועדפים שלי</span>
          </div>
          {favorites.length > 0 && (
            <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full">
              {favorites.length}
            </span>
          )}
        </Link>

        <Link
          to="/user/profile"
          onClick={() => setShowUserDropdown(false)}
          className="flex items-center gap-3 w-full px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-colors"
        >
          <Settings className="w-4 h-4" />
          <span className="font-medium">ניהול פרופיל</span>
        </Link>

        <div className="border-t border-gray-200 my-2"></div>

        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span className="font-medium">התנתק</span>
        </button>
      </div>
    </div>
  );

  return (
    <nav 
      className={`
        fixed top-0 left-0 right-0 z-50
        transition-all duration-700
       ${isScrolled 
            ? 'bg-black backdrop-blur-xl shadow-2xl border-b border-cyan-400/30' 
            : 'bg-gradient-to-br from-blue-900 via-indigo-900/60 to-blue-900 backdrop-blur-xl shadow-2xl border-b border-cyan-400/30'}
        `}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex h-10 md:h-16 items-center justify-between gap-4">
          
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="flex items-center group">
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-r from-cyan-400/30 via-blue-400/30 to-purple-400/30 rounded-2xl blur-lg opacity-0 group-hover:opacity-70 transition-all duration-500"></div>
                <img 
                  src="/Logo.png" 
                  alt="פרו גמל נט" 
                  className="relative h-6 w-6 md:h-10 md:w-10 object-contain rounded-xl shadow-lg group-hover:scale-110 transition-all duration-500"
                />
              </div>
              <div className="mr-2 md:mr-3 text-sm md:text-xl font-bold text-white drop-shadow-lg group-hover:text-cyan-300 transition-colors duration-300 truncate">
                <span className="md:hidden">גמלנט AI</span>
                <span className="hidden md:inline">גמלנט AI</span>
              </div>
            </Link>
          </div>
          
          {/* Desktop Main Menu */}
     <div className="hidden lg:flex items-center gap-2 flex-1 justify-center max-w-6xl">
  {mainMenuItems.map((item) => {
    const isActive = location.pathname === item.href;
    return (
      <Link
        key={item.title}
        to={item.href}
        className={`
          relative flex items-center px-2 py-1.5 rounded-lg text-xs font-medium
          transition-all duration-300 group overflow-hidden border flex-shrink-0
          ${isActive 
            ? 'text-white bg-gradient-to-r from-cyan-500/80 to-blue-500/80 shadow-lg shadow-cyan-500/25 border-cyan-400/50' 
            : 'text-white/90 hover:text-white hover:bg-white/10 border-white/20 hover:border-cyan-400/50'}
        `}
      >
        {!isActive && (
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/10 via-blue-400/10 to-purple-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
        )}
        
        <span className="relative z-10 flex items-center">
          {item.icon && <span className="ml-1 group-hover:scale-110 transition-transform duration-300">{item.icon}</span>}
          <span className="text-[10px] font-bold whitespace-nowrap">{item.title}</span>
        </span>
        
        {isActive && (
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full shadow-lg shadow-cyan-400/50"></div>
        )}
      </Link>
    );
  })}
</div>

          {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            
            {actionItems.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.title}
                  to={item.href}
                  className={`
                    relative flex items-center px-3 py-1.5 rounded-lg text-xs font-bold
                    bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 text-slate-900
                    hover:from-yellow-300 hover:via-orange-300 hover:to-red-300
                    transform hover:scale-105 transition-all duration-300
                    shadow-lg hover:shadow-xl shadow-yellow-400/30
                    overflow-hidden group border border-yellow-300/50 flex-shrink-0
                  `}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/30 via-transparent to-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <span className="relative z-10 flex items-center">
                    {item.icon && <span className="ml-1.5 group-hover:scale-110 transition-transform duration-300">{item.icon}</span>}
                    <span className="text-xs font-bold">{item.title}</span>
                  </span>
                </Link>
              );
            })}

            {/* User Button */}
            <div className="relative flex-shrink-0">
              {isAuthenticated ? (
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="
                    relative flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium
                    bg-white/10 hover:bg-white/20 border border-white/20 hover:border-cyan-400/50
                    text-white hover:text-cyan-300 transition-all duration-300 group
                  "
                >
                  <div className="w-5 h-5 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <User className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-xs font-semibold max-w-16 truncate">
                    {userProfile?.firstName || 'משתמש'}
                  </span>
                  {favorites.length > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-1 py-0.5 min-w-[16px] text-center flex-shrink-0">
                      {favorites.length}
                    </span>
                  )}
                  <ChevronDown className={`w-3 h-3 transition-transform duration-200 flex-shrink-0 ${showUserDropdown ? 'rotate-180' : ''}`} />
                </button>
              ) : (
                <Link
                  to="/auth/login"
                  className="
                    relative flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium
                    bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400
                    text-white border border-cyan-400/50 hover:border-cyan-300/70
                    transform hover:scale-105 transition-all duration-300
                    shadow-lg hover:shadow-xl shadow-cyan-500/30 flex-shrink-0
                  "
                >
                  <LogIn className="w-4 h-4" />
                  <span className="text-xs font-bold">התחבר</span>
                </Link>
              )}

              {isAuthenticated && showUserDropdown && <UserDropdown />}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2 flex-shrink-0">
            {/* Home & Search Buttons - Mobile Only */}
            <Link
              to="/"
              className="relative p-1.5 rounded-lg border border-white/30 text-white hover:text-cyan-300 hover:bg-white/10 hover:border-cyan-400/50 transition-all duration-300 group"
            >
              <Home className="h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
            </Link>
            
            <Link
              to="/route-scanner"
              className="relative p-1.5 rounded-lg border border-white/30 text-white hover:text-cyan-300 hover:bg-white/10 hover:border-cyan-400/50 transition-all duration-300 group"
            >
              <Search className="h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
            </Link>
            
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="relative p-1.5 rounded-lg border border-white/30 text-white hover:text-cyan-300 hover:bg-white/10 hover:border-cyan-400/50 transition-all duration-300 group"
              aria-label={isOpen ? 'סגור תפריט' : 'פתח תפריט'}
            >
              <div className="group-hover:scale-110 transition-transform duration-300">
                {isOpen ? <X className="h-4 w-4" /> : <Grid3X3 className="h-4 w-4" />}
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu - קומפקטי יותר */}
      {isOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-slate-900/95 backdrop-blur-xl shadow-2xl border-t border-cyan-400/30 max-h-screen overflow-y-auto">
          <div className="px-3 py-3 space-y-2">
            
            {/* User section - קומפקטי */}
            {isAuthenticated ? (
              <div className="p-3 bg-white/10 rounded-lg border border-white/20 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-md flex items-center justify-center">
                    <User className="w-3 h-3 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-xs truncate">
                      {userProfile?.displayName || 'משתמש'}
                    </p>
                  </div>
                  {favorites.length > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                      {favorites.length}
                    </span>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <Link
                    to="/user/profile"
                    onClick={() => setIsOpen(false)}
                    className="flex-1 text-center py-1.5 px-2 bg-white/10 hover:bg-white/20 text-white text-xs rounded-md transition-colors"
                  >
                    פרופיל
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsOpen(false);
                    }}
                    className="flex-1 text-center py-1.5 px-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs rounded-md transition-colors"
                  >
                    התנתק
                  </button>
                </div>
              </div>
            ) : (
              <Link
                to="/auth/login"
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg border border-cyan-400/50 mb-3"
              >
                <LogIn className="w-4 h-4" />
                <span>התחבר</span>
              </Link>
            )}

       {/* גריד קומפקטי לפריטי תפריט - 2 עמודות */}
<div className="grid grid-cols-2 gap-2">
  {/* כפתור סורק מסלולים - תמיד ראשון */}
  <Link
    to="/route-scanner"
    className="flex flex-col items-center justify-center p-3 rounded-lg text-xs font-medium transition-all duration-300 border min-h-[60px] bg-gradient-to-r from-green-500/80 to-emerald-500/80 text-white shadow-lg border-green-400/50"
    onClick={() => setIsOpen(false)}
  >
    <Search className="w-5 h-5 mb-1" />
    <span className="text-center leading-tight">סורק מסלולים</span>
  </Link>

  {mainMenuItems.slice(2).map((item) => { // מדלג על דף הבית וסורק
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.title}
                    to={item.href}
                    className={`
                      flex flex-col items-center justify-center p-3 rounded-lg text-xs font-medium
                      transition-all duration-300 border min-h-[60px]
                      ${isActive 
                        ? 'text-white bg-gradient-to-r from-cyan-500/80 to-blue-500/80 shadow-lg border-cyan-400/50' 
                        : 'text-white/90 hover:text-white hover:bg-white/10 border-white/20 hover:border-cyan-400/50'
                      }
                    `}
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="mb-1">
                      {item.icon}
                    </span>
                    <span className="text-center leading-tight">{item.title}</span>
                  </Link>
                );
              })}
            </div>

            {/* Action items - בשורה נפרדת */}
            <div className="pt-2 border-t border-white/20">
              <div className="grid grid-cols-2 gap-2">
                {actionItems.map((item) => (
                  <Link
                    key={item.title}
                    to={item.href}
                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-gradient-to-r from-yellow-400 to-orange-400 text-slate-900 shadow-lg border-yellow-300/50"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.icon}
                    <span>{item.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;